/**
 * useAuthorityManagement Hook
 * 권한 설정 관련 로직 관리
 */
import { useState, useEffect, useCallback } from 'react';
import { authorityApi } from '../api/authorityApi';
import { Authority, AuthorityFormData } from '../types/authority.types';
import { isStandardPermission } from '../utils/authorityUtils';
import { useToast } from '@/shared/components/feedback/Toast/ToastContext';
import { ToastType } from '@/shared/components/feedback/Toast/Toast.types';
import { getItem } from '@/shared/utils/storage';

export const useAuthorityManagement = () => {
    const { showToast } = useToast();

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [authorities, setAuthorities] = useState<Authority[]>([]);

    const fetchAuthorities = useCallback(async (includeStandard = true) => {
        try {
            setLoading(true);
            setError(null);
            const data = await authorityApi.getAuthorityList(includeStandard);
            setAuthorities(data);
        } catch {
            const message = '권한 목록 조회에 실패했습니다.';
            setError(message);
            showToast(message, { type: ToastType.ERROR });
        } finally {
            setLoading(false);
        }
    }, [showToast]);

    const createAuthority = useCallback(async (data: AuthorityFormData): Promise<boolean> => {
        try {
            setSaving(true);
            const userId = getItem<string>('USER_ID') || '';

            const response = await authorityApi.createAuthority({
                prmCode: data.prmCode,
                prmName: data.prmName,
                prmDesc: data.prmDesc,
                baseUserType: data.baseUserType,
                accessPages: data.accessPages,
                insertUserId: userId,
            });

            if (response.success) {
                showToast('권한이 등록되었습니다.', { type: ToastType.SUCCESS });
                await fetchAuthorities();
                return true;
            }

            showToast(response.message || '권한 등록에 실패했습니다.', { type: ToastType.ERROR });
            return false;
        } catch {
            showToast('권한 등록에 실패했습니다.', { type: ToastType.ERROR });
            return false;
        } finally {
            setSaving(false);
        }
    }, [showToast, fetchAuthorities]);

    const updateAuthority = useCallback(async (data: AuthorityFormData): Promise<boolean> => {
        if (isStandardPermission(data.prmCode)) {
            showToast('표준 권한은 수정할 수 없습니다.', { type: ToastType.ERROR });
            return false;
        }

        try {
            setSaving(true);
            const userId = getItem<string>('USER_ID') || '';

            const response = await authorityApi.updateAuthority({
                prmCode: data.prmCode,
                prmName: data.prmName,
                prmDesc: data.prmDesc,
                baseUserType: data.baseUserType,
                accessPages: data.accessPages,
                updateUserId: userId,
            });

            if (response.success) {
                showToast('권한이 수정되었습니다.', { type: ToastType.SUCCESS });
                await fetchAuthorities();
                return true;
            }

            showToast(response.message || '권한 수정에 실패했습니다.', { type: ToastType.ERROR });
            return false;
        } catch {
            showToast('권한 수정에 실패했습니다.', { type: ToastType.ERROR });
            return false;
        } finally {
            setSaving(false);
        }
    }, [showToast, fetchAuthorities]);

    const deleteAuthority = useCallback(async (prmCode: string): Promise<boolean> => {
        if (isStandardPermission(prmCode)) {
            showToast('표준 권한은 삭제할 수 없습니다.', { type: ToastType.ERROR });
            return false;
        }

        try {
            setSaving(true);
            const userId = getItem<string>('USER_ID') || '';

            const response = await authorityApi.deleteAuthority(prmCode, userId);

            if (response.success) {
                showToast('권한이 삭제되었습니다.', { type: ToastType.SUCCESS });
                await fetchAuthorities();
                return true;
            }

            showToast(response.message || '권한 삭제에 실패했습니다.', { type: ToastType.ERROR });
            return false;
        } catch {
            showToast('권한 삭제에 실패했습니다.', { type: ToastType.ERROR });
            return false;
        } finally {
            setSaving(false);
        }
    }, [showToast, fetchAuthorities]);

    useEffect(() => {
        fetchAuthorities();
    }, [fetchAuthorities]);

    return {
        loading,
        saving,
        error,
        authorities,
        fetchAuthorities,
        createAuthority,
        updateAuthority,
        deleteAuthority,
    };
};
